import { RefObject, useLayoutEffect } from 'react';

/**
 * A full-bleed page's height (R6 follow-up): what is left of the viewport
 * below wherever the top bar, the banners and the form bar have put its
 * top edge. The Workflow canvas and the Runs table use it so rail · canvas,
 * and table · timeline, fill the screen with no page scroll; so do
 * Fields, Logic and Document, whose Outline · canvas · inspector would
 * otherwise grow to a long form's length and scroll away with it.
 *
 * Measured rather than written as a `calc`, because the chrome above is not
 * one number: a trial banner, the folder-news banner or the support strip
 * each add a row, and a wrong constant leaves either a gap or a page scroll.
 * Re-measured on resize and whenever anything drawn ABOVE the page changes
 * size — the banners, the form bar — which is what is watched, not the
 * body, whose min-height hides a bar that got shorter. Off on a phone,
 * where the M6 layouts scroll the page.
 */
export function usePageHeight(ref: RefObject<HTMLElement>, on: boolean, mounted = true) {
  useLayoutEffect(() => {
    // `mounted` is in the deps so this runs again once "Loading…" has given
    // way to the page and the ref points at something.
    const el = ref.current;
    if (!el || !mounted) return;
    if (!on) { el.style.height = ''; return; }
    const set = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      el.style.height = `${Math.max(0, window.innerHeight - top)}px`;
    };
    set();
    // Once more after this frame: the bar's rows and the banners settle a
    // paint later than the page mounts.
    const raf = window.requestAnimationFrame(set);
    window.addEventListener('resize', set);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    for (let node: Element | null = el; node && !node.classList.contains('ad-wrap'); node = node.parentElement) {
      for (let above = node.previousElementSibling; above; above = above.previousElementSibling) ro?.observe(above);
    }
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', set);
      ro?.disconnect();
      el.style.height = '';
    };
  }, [ref, on, mounted]);
}
